import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminFestivalsService } from '../services/admin.festivals.service'
import type { FestivalDocument } from '@/types'
import type { FestivalFormValues } from '../types'

interface UseAdminFestivalsResult {
  festivals: Array<FestivalDocument & { id: string }>
  loading: boolean
  error: string | null
  createFestival: (data: FestivalFormValues) => Promise<string>
  updateFestival: (id: string, data: Partial<FestivalFormValues>) => Promise<void>
  deleteFestival: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminFestivals(): UseAdminFestivalsResult {
  const [festivals, setFestivals] = useState<Array<FestivalDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminFestivalsService
      .getAllFestivals()
      .then(setFestivals)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load festivals'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createFestival = useCallback(
    async (data: FestivalFormValues): Promise<string> => {
      const id = await adminFestivalsService.createFestival(data)
      refresh()
      return id
    },
    [refresh]
  )

  const updateFestival = useCallback(
    async (id: string, data: Partial<FestivalFormValues>): Promise<void> => {
      await adminFestivalsService.updateFestival(id, data)
      refresh()
    },
    [refresh]
  )

  const deleteFestival = useCallback(
    async (id: string): Promise<void> => {
      await adminFestivalsService.deleteFestival(id)
      refresh()
    },
    [refresh]
  )

  return { festivals, loading, error, createFestival, updateFestival, deleteFestival, refresh }
}
